/**
 * categorize.js — guess which Today list an item belongs on, from its name.
 *
 * Kitchen rule of thumb the lists follow:
 *   🧑‍🍳 chef      — the chef's pantry: dals & pulses, nuts, flours / rava, spices & masalas,
 *                   curry leaves, chillies, ginger & garlic, fresh herbs, rice, oils & ghee,
 *                   salt, tamarind, jaggery
 *   🛒 groceries  — everything else bought as-is (produce, dairy, meat, drinks, bottled
 *                   sauces, frozen breads & snacks, sugar, coffee/tea)
 *   🥡 supplies   — to-go packaging & disposables (cups, lids, boxes, bags, gloves, foil…)
 *   🔪 prep       — made ready ahead, no finished dish: chop / cut / marinate / soak / portion /
 *                   batter / refill / raita & lassi, plus any par-pan of raw produce
 *   🍳 cooking    — cooked on the stove: curries, gravies, house sauces, chutneys, rice, dal…
 *   🧽 cleaning   — closing duties
 *
 * Pure + ordered: the FIRST tier whose pattern matches wins, so specific phrases sit
 * above generic words ("Chutney cups" → supplies before "chutney" → cooking; "Soy sauce"
 * → groceries before "sauce" → cooking; "Chopped ginger" → prep before "ginger" →
 * groceries). A par-pan note (e.g. "1/6") means an in-house pan, so a raw-ingredient
 * match becomes prep ("Spring onions · 1/12" is chopped, not bought).
 *
 * Returns `{ id, match }` (match = the word that decided it, shown as the reason) or
 * null when nothing is confidently known — callers then keep the current list.
 */

const w = words => new RegExp(`\\b(?:${words.join('|')})\\b`, 'i');

// Ordered tiers — the FIRST match wins. `MEAT_STEP` marks where a raw-meat name with no
// dish word is resolved as a purchase (after prep words, so "Marinate chicken" is prep).
const MEAT_STEP = 'meat';
const TIERS = [
  // Disposables whose names contain a cleaning word ("Trash bags" is bought, not a task).
  ['supplies', w(['trash bags?', 'garbage bags?', 'bin liners?'])],
  ['cleaning', w([
    'clean(?:ing|ed)?', 'wipe', 'mop', 'sweep', 'trash', 'garbage', 'sanitiz(?:e|er)', 'scrub(?:bers?)?',
    'wash dishes', 'coin check', 'dollar ?/ ?coin check', 'cash (?:count|check)',
    'clorox', 'bleach', 'degreaser', 'detergent', 'dish ?soap', 'soap', 'black diamond', 'sponges?',
  ])],
  ['supplies', w([
    'cups?', 'lids?', 'boxes', 'to-?go ?box(?:es)?', 'containers?', 'bags?', 'gloves?', 'foil', 'napkins?', 'plates?',
    'cutlery', 'spoons?', 'forks?', 'straws?', 'deli sheets?', 'butter papers?', 'labels?', 'sle+ves?', 'tape',
    'water bottles?', 'ketchup', 'paper towels?', 'cling ?wrap', 'bowls?', 'tissues?',
    'trays?', 'half ?trays?', '\\d/\\d ?trays?', 'bills', 'coins?',
  ])],
  // Bought in a bottle / can / packet — beats the generic "sauce" → cooking, "chat" /
  // "bhel" → prep, and the chef's "powder" / "chilli" / "garlic" / "rice" words below.
  ['groceries', w([
    'soy sauce', 'green chil+i(?:es)? sauce', 'red chil+i(?:es)? sauce', 'hot (?:&|and) sweet(?: chil+i)?(?: sauce)?',
    'sriracha', 'vinegar', 'food colou?r', 'ajinomoto', 'bou?ill?on', 'bullion', 'stock cubes?', 'pulp',
    'coffee', 'tea(?: powder| bags?)?', 'baking (?:soda|powder)', 'custard powder', 'milk powder',
    'thum(?:b)?s? ?up', 'coke', 'pepsi', 'sprite', 'fanta', 'limca', 'maaza', 'frooti', 'soda', 'cold drinks?',
    'drinks', 'juice', 'boost', 'horlicks', 'bournvita', 'kulfi', 'ice cream', 'gulab jamun',
    'naans?', 'par(?:o|a)t+h?as?', 'roti', 'tortillas?', 'wraps', 'frozen', 'samosas?',
    'sev', 'papdi', 'bhel (?:puri |poori )?mix', 'mixture', 'murmura', 'puffed rice',
  ])],
  // The chef's packaged pantry — beats "dal" → cooking, "chat" → prep, "curry" → cooking and
  // "peeled" → prep. (Loose spices / produce are caught by CHEF_RAW at the very end.)
  ['chef', w([
    '(?:chana|urad|toor|moong|mung|masoor|green moong) ?dal', 'pesarlu', 'pesara',
    'powder', '(?:biryanis?|garam|cha+t|pani ?poori|sambar|rasam|tandoori|chole|pav bhaji|kitchen king|curry) masala',
    'masala (?:box|dab+as?|powder|packets?)', 'rava', 'sooji', 'semolina', 'curry leaves',
    'peeled garlic', 'garlic peeled', 'ginger[- ]garlic(?: paste)?', 'kas(?:oo|u)ri methi',
  ])],
  // Unmistakably cooked — beats prep's "pieces" / "veggies" (and the chef's "masala").
  ['cooking', w(['cooked', 'saut[eé]ed', 'gravy', 'dosa masala', 'masala dosa'])],
  ['prep', w([
    'chopped', 'chop', 'cut(?:ting)?', 'sliced?', 'diced?', 'cubes?', 'cubed', 'minced', 'grated', 'peeled', 'julienne',
    'marinated?', 'marination', 'soak(?:ed)?', 'boil(?:ed)?', 'pieces', 'pcs', 'balls?',
    'veggies', 'batters?', 'dough', 'refills?', 'raita', 'sweet curd', 'lassi', 'pani ?poori water',
    'chaat(?! sauces?)', 'chat(?! sauces?)', 'bhel', 'poori', 'puri', 'thaw', 'portion(?:ed)?',
  ])],
  [MEAT_STEP],
  ['cooking', w([
    'curry', 'sauces?', 'chutneys?', 'kurma', 'korma', 'keema', 'biryani', 'pulao', 'fried rice',
    '(?:jeera|lemon|tamarind|curd|coconut|ghee|steamed) rice', 'dal', 'sambar', 'rasam', 'fry', 'fried',
    'manchurian?', '65', 'tikka', 'gongura', 'pickle', 'halwa', 'kheer', 'payasam', 'pongal', 'raju',
  ])],
  // A meat name that reached here carries a dish word (checked in MEAT_STEP) → cooked.
  ['cooking', w(['chicken', 'mutton', 'lamb', 'goat', 'fish', 'shrimp', 'prawns?'])],
];

// Raw ingredients — checked LAST, after every prepared-food tier, the chef's pantry first
// (so "White peas" beats "peas", "Kasoori methi" beats "methi", "Coconut powder" → chef but
// "Coconut milk" → groceries). Common kitchen spellings included (chillies / chilles,
// maidha, cardamon, cinamon, serano, bayleaf, kaju…).
const CHEF_RAW = w([
  'dal', 'lentils?', 'white peas', 'rajma', 'chickpeas?', 'kabuli chana', 'chana', 'peanuts?', 'cashews?', 'kaju',
  'flour', 'atta', 'maid?h?a', 'besan', 'ragi', 'rice', 'basmati', 'sona ?masuri',
  'salt', 'sea ?salt', 'jeera', 'cumin', 'elachi', 'cardamo[mn]s?', 'star anise', 'cloves?', 'cin+am+on',
  'bay ?lea(?:f|ves)', 'karam', 'turmeric', 'haldi', 'masala', '(?:mustard|methi|coriander|fennel|sesame) seeds?',
  'saunf', 'inguva', 'hing', 'asafoetida', 'pepper ?corns?', 'black pepper',
  'chil+(?:i|ie|e)s?', 'mirchi', 'ser+ano', 'curry leaf', 'ginger', 'garlic', 'cilantro', 'coriander', 'mint',
  'tamarind', 'jaggery', 'oil', 'ghee',
]);
const RAW = w([
  'milk', 'butter', '(?:sour |whipping |heavy (?:whipping )?)?cream', 'cheese', 'paneer', 'yogh?urt',
  'curd', 'eggs?', 'sugar', 'almonds?', 'raisins?', 'onions?', 'tomato(?:es)?',
  'potato(?:es)?', 'lemons?', 'limes?', 'spring onions?', 'cabbage', 'carrots?', 'peas', 'capsicum', 'bell peppers?',
  'cauliflower', 'gobi', 'lettuce', 'cucumber', 'beans', 'okra', 'bhindi',
  'eggplant', 'brinjal', 'spinach', 'palak', 'methi', 'noodles', 'bread', 'jackfruit',
]);
// A dish or product made FROM a chef ingredient ("Green chilli sauce", "Garlic naan") —
// never the chef's own pantry, even when it names one.
const CHEF_PRODUCT = w(['sauces?', 'chutneys?', 'naans?', 'curry', 'gravy', 'pickle', 'rice']);

// Raw meat ("Chicken bone-in", "Boneless chicken") is a purchase unless a dish word says otherwise.
const MEAT = w(['chicken', 'mutton', 'goat', 'lamb', 'fish', 'shrimp', 'prawns?']);
const DISH = w(['curry', 'gravy', 'fry', 'fried', 'roast', '65', 'tikka', 'biryani', 'raju', 'masala', 'sauce', 'chutney', 'manchurian?', 'kebab', 'kabab', 'lollipop']);

/** A par-pan note like "1/6", "1/12", "½" — the item lives in a hotel pan in-house. */
export const isParNote = note => /^\s*(?:\d+\s*\/\s*\d+|[½⅓¼⅙⅛])\s*$/.test(String(note || ''));

const clean = s => String(s || '').toLowerCase().replace(/[’']/g, '').replace(/\s+/g, ' ').trim();

export function suggestCategory(name, { note } = {}) {
  const n = clean(name);
  if (!n) return null;
  const par = isParNote(note);
  // A raw ingredient in a par-pan was prepped in-house (chopped, portioned, boiled).
  const rawOrPrep = (match, list = 'groceries') => ({ id: par ? 'prep' : list, match });

  for (const [id, re] of TIERS) {
    if (id === MEAT_STEP) {
      const m = n.match(MEAT);
      if (m && !DISH.test(n)) return rawOrPrep(m[0]);
      continue;
    }
    const m = n.match(re);
    if (m) return { id, match: m[0] };
  }
  const chef = n.match(CHEF_RAW);
  if (chef) return rawOrPrep(chef[0], 'chef');
  const raw = n.match(RAW);
  return raw ? rawOrPrep(raw[0]) : null;
}

/**
 * Does Chef Groceries take this name over from the list it was noted in? Yes for the
 * chef's own ingredients ("Besan", "Toor dal", "Curry leaves") and prepped forms of them
 * ("Coriander chopped", "Ginger pieces") — never for a dish or product made from one
 * ("Green chilli sauce", "Garlic naan", "Tamarind chutney").
 */
export function isChefItem(name) {
  const s = suggestCategory(name);
  if (!s) return false;
  if (s.id === 'chef') return true;
  const n = clean(name);
  return s.id === 'prep' && CHEF_RAW.test(n) && !CHEF_PRODUCT.test(n);
}

/** List ids whose name rule can claim items noted elsewhere → the rule (see logic/lists.js). */
export const CLAIM_RULES = { chef: isChefItem };

/**
 * Plan the Firestore writes that make each closing item land on `finals.get(item.id)`
 * (items not in `finals` keep their current list), keeping the tree tidy: in every
 * sub-section that has a change, the sub-section's own target becomes the list most of
 * its items now use, and only the odd ones out carry an item-level override. Untouched
 * sub-sections are never rewritten.
 *
 * @param subsections [{ id, sectionId, target }]
 * @param sections    [{ id, target }]
 * @param items       [{ id, subsectionId, target }]
 * @param finals      Map<itemId, categoryId>
 * @param order       category ids, for a stable tie-break
 * @returns {{ subsections: [{id, target}], items: [{id, target}] }}
 */
export function planRetarget({ subsections, sections, items, finals, order = [] }) {
  const secTarget = new Map(sections.map(s => [s.id, s.target]));
  const out = { subsections: [], items: [] };
  subsections.forEach(sub => {
    const subEff = sub.target || secTarget.get(sub.sectionId);
    const list = items.filter(i => i.subsectionId === sub.id);
    const cur = i => i.target || subEff;
    const fin = i => finals.get(i.id) || cur(i);
    if (!list.some(i => fin(i) !== cur(i))) return;

    // New default = the list most items now use; ties go to whichever needs the fewest
    // writes, then the current default, then category order.
    const counts = new Map();
    list.forEach(i => counts.set(fin(i), (counts.get(fin(i)) || 0) + 1));
    const top = Math.max(...counts.values());
    const writesFor = eff => list.filter(i => (fin(i) === eff ? null : fin(i)) !== (i.target || null)).length;
    const cost = eff => writesFor(eff) + (eff === subEff ? 0 : 1);
    const rank = id => (id === subEff ? -1 : order.indexOf(id));
    const newEff = [...counts.keys()].filter(id => counts.get(id) === top)
      .sort((a, b) => cost(a) - cost(b) || rank(a) - rank(b))[0];
    if (newEff !== subEff) out.subsections.push({ id: sub.id, target: newEff });

    list.forEach(i => {
      const want = fin(i) === newEff ? null : fin(i);
      if (want !== (i.target || null)) out.items.push({ id: i.id, target: want });
    });
  });
  return out;
}
